import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'utils/uuid';
import SortableList, { SortableItem } from 'react-easy-sort';
import arrayMove from 'utils/arrayMove';

import { Button, Typography } from './base';
import { Photo, Infobox } from './rich';
import Icon, { ICON_TYPE } from './base/icon';

import useUploaderContext from '../context/uploaderContext';
import useEditorContext from '../context/editorContext';
import useEditorItemContext from '../context/editorItemContext';
import PhotoWellItem from './photoWellItem';

const BUTTON = (numberOfUploadedPhotos = 0, maximumMorePhotos = 1) => {
  if (maximumMorePhotos === 0) {
    return 'Photos uploaded';
  }

  if (maximumMorePhotos === 1) {
    return 'Add Photo';
  }

  if (numberOfUploadedPhotos === 0) {
    return 'Add Photos';
  }

  return 'Add Photos';
};

const PhotoWell = props => {
  const editorContext = useEditorContext();
  const { item } = useEditorItemContext();

  const { photos } = item;

  const uploader = useUploaderContext();

  const handleOnChangePhotos = newPhotos => {
    editorContext.setPhotos(props.id, newPhotos);
  };

  const minimumRequiredNumberOfPhotos = props.minimumNumberOfPhotos - photos.length;
  const maximumRequiredNumberOfPhotos = props.maximumNumberOfPhotos - photos.length;

  let handleOnRemovePhoto;
  if (props.minimumNumberOfPhotos) {
    handleOnRemovePhoto = index => {
      handleOnChangePhotos(photos.filter((_, i) => i !== index));
    };
  }

  let handleOnDuplicatePhoto;
  if (maximumRequiredNumberOfPhotos > 0) {
    handleOnDuplicatePhoto = index => {
      handleOnChangePhotos([
        ...photos.slice(0, index + 1),
        { ...photos[index], key: uuid() },
        ...photos.slice(index + 1),
      ]);
    };
  }

  let handleOnSelectPhoto;
  if (props.onSelectPhotos) {
    handleOnSelectPhoto = index => {
      props.onSelectPhotos([photos[index]]);
    };
  }

  const handleOnAddPhotosClick = e => {
    e.preventDefault();

    uploader.choosePhotos(maximumRequiredNumberOfPhotos, newPhoto => {
      editorContext.addPhotos(props.id, [newPhoto]);
    });
  };

  const handleOnPhotosSortEnd = (oldIndex, newIndex) => {
    handleOnChangePhotos(arrayMove(photos, oldIndex, newIndex));
  };

  return (
    <>
      {props.children}

      {props.sortable && photos.length > 0 && props.minimumNumberOfPhotos > 1 && (
        <Infobox mb={4}>
          <Typography.Heading size={3} mb={1}>
            Arrange photos
          </Typography.Heading>
          <Typography.Paragraph>
            Move photos around by dragging and dropping. Duplicate photo by clicking on [+]. Remove
            photo by clicking on [-].
          </Typography.Paragraph>
        </Infobox>
      )}

      <SortableList onSortEnd={handleOnPhotosSortEnd} allowDrag={props.sortable}>
        <Photo.List mt={4}>
          {(props.uploadEnabled || maximumRequiredNumberOfPhotos > 0) && (
            <Photo.Item type="add" onClick={handleOnAddPhotosClick}>
              <Photo.ImageWrapper>
                <Typography.Paragraph>
                  <Button disabled={uploader.isLoading} circular outline>
                    <Icon type={ICON_TYPE.ADD} color="primary" width={8} height={8} />
                  </Button>
                  <Typography.Heading as="span" size={2} mt={1} textDecoration="none">
                    {BUTTON(photos.length, maximumRequiredNumberOfPhotos)}
                  </Typography.Heading>
                </Typography.Paragraph>
              </Photo.ImageWrapper>
            </Photo.Item>
          )}
          {photos.map((photo, index) => (
            <SortableItem key={photo.key}>
              <PhotoWellItem
                cover={props.numberOfCoverPhotos > 0 && index === 0}
                photo={photo}
                pages={props.pages
                  .filter(page => page.placeholders.find(p => p.data?.photo_key === photo.key))
                  .filter(p => !!p)}
                onRemove={handleOnRemovePhoto ? () => handleOnRemovePhoto(index) : undefined}
                onDuplicate={
                  handleOnDuplicatePhoto ? () => handleOnDuplicatePhoto(index) : undefined
                }
                onSelect={handleOnSelectPhoto ? () => handleOnSelectPhoto(index) : undefined}
              />
            </SortableItem>
          ))}
        </Photo.List>
      </SortableList>
    </>
  );
};

PhotoWell.propTypes = {
  id: PropTypes.string,
  numberOfCoverPhotos: PropTypes.number,
  minimumNumberOfPhotos: PropTypes.number,
  maximumNumberOfPhotos: PropTypes.number,
  onSelectPhotos: PropTypes.func,
  sortable: PropTypes.bool,
  pages: PropTypes.array,
  uploadEnabled: PropTypes.bool,
  children: PropTypes.node,
};

PhotoWell.defaultProps = {
  sortable: true,
  uploadEnabled: false,
  numberOfCoverPhotos: 0,
  pages: [],
};

export default PhotoWell;
